import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Drawer,
  IconButton,
  Skeleton,
  Typography,
  alpha,
  darken,
  lighten,
} from "@mui/material";
import {
  Add as AddIcon,
  AccessTime as AccessTimeIcon,
  Biotech as BiotechIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  EventBusy as EventBusyIcon,
  HourglassBottom as HourglassIcon,
  MedicalInformation as MedicalInfoIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  FaCalendarAlt,
  FaUserMd,
  FaCheckCircle,
  FaStethoscope,
  FaRedoAlt,
} from "react-icons/fa";
import useAuth from "../../hooks/useAuth";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useClinicTheme } from "../../theme";
import {
  ActionButton,
  DrawerHeader,
  DrawerFooter,
  FormAutocomplete,
  FormSelect,
  FormTextField,
  TrCalendarPicker,
  TrTimePicker,
  SegmentedControl,
} from "../../components/atoms";
import { PageHeaderBar, PromptDialog } from "../../components/molecules";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import {
  useCancelAppointmentMutation,
  useCreateAppointmentMutation,
  useDeleteAppointmentMutation,
  useListClinicAppointmentsMutation,
  useListClinicDoctorsMutation,
  useListPatientsMutation,
  useUpdateAppointmentLabTestsMutation,
  useUpdateAppointmentDetailsMutation,
} from "../../store/secretaryApi";
import { useListLabTestsMutation } from "../../store/systemVariablesApi";
import { useListDoctorAppointmentsMutation } from "../../store/doctorApi";
import AppointmentDetailDrawer from "./PatientDetail/AppointmentDetailDrawer";
import { IN_EXAM_TONE, ExamLiveBadge, examLiveKeyframesSx } from "./AppointmentsPage/examLive";
import AppointmentListView from "./AppointmentsPage/AppointmentListView";
import AppointmentServiceFields from "../../components/organisms/Billing/AppointmentServiceFields";
import EmptyState from "../../components/atoms/EmptyState";
import { staffFullName, staffLabel, staffRoleName } from "../../utils/staffName";

import { ACCENTS, COLORS, TYPO } from "../../styles/uiConstants";
import useTerms from "../../hooks/useTerms";
import { useClinicModule } from "../../hooks/useClinicModule";

const APPOINTMENT_TYPE_OPTIONS = [
  { value: "ilk_muayene", label: "İlk Muayene" },
  { value: "kontrol", label: "Kontrol" },
];

const pad2 = (n) => String(n).padStart(2, "0");

const toDateStr = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};

const toTimeStr = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
};

const combineDatetime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return "";
  return `${dateStr}T${timeStr}`;
};

/** Seçilen tarihe göre klinik çalışma saatlerini bul */
const getScheduleForDate = (dateStr, clinicSchedule) => {
  if (!dateStr || !clinicSchedule?.length) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const dayName = DAY_INDEX_TO_TR[d.getDay()];
  const entry = clinicSchedule.find((s) => s.day === dayName);
  if (!entry) return { dayName, isClosed: true };
  return { dayName, isClosed: false, startTime: entry.startTime, endTime: entry.endTime };
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const fmtDayTitle = (d) => {
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      weekday: "short",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch (_) {
    return d.toLocaleDateString("tr-TR");
  }
};

/** Pazartesi–Pazar aralığı; hafta görünümündeki veri aralığıyla aynı. */
const fmtWeekTitle = (d) => {
  const start = new Date(d);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const left = start.toLocaleDateString("tr-TR", sameMonth ? { day: "numeric" } : { day: "numeric", month: "short" });
  const right = end.toLocaleDateString("tr-TR", { day: "numeric", month: sameMonth ? "long" : "short", year: "numeric" });
  return `${left} – ${right}`;
};

const fmtTime = (d) => {
  const x = new Date(d);
  return x.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
};

const tzOffsetLabel = () => {
  const minutes = new Date().getTimezoneOffset(); // e.g. -180 for GMT+3
  const sign = minutes <= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const h = String(Math.floor(abs / 60)).padStart(2, "0");
  const m = String(abs % 60).padStart(2, "0");
  return `GMT${sign}${h}:${m}`;
};

const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

/** Randevular görünüm kapsamı tercihi (mine | all), tarayıcı başına */
const SCOPE_STORAGE_KEY = "pupill.appointments.scope";


/** JS getDay() → Türkçe gün adı eşleşmesi */
const DAY_INDEX_TO_TR = {
  1: "Pazartesi",
  2: "Salı",
  3: "Çarşamba",
  4: "Perşembe",
  5: "Cuma",
  6: "Cumartesi",
  0: "Pazar",
};

/** Saat string ("09:00") → saat number (9) */
const parseHour = (t) => {
  if (!t) return null;
  const h = parseInt(t.split(":")[0], 10);
  return Number.isFinite(h) ? h : null;
};

// Gün görünümünde bir saatin alabileceği en az yükseklik (px)
const MIN_HOUR_PX = 64;

export default function Appointments() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const canCreateAppt = useHasPermission("appointments:create");
  const terms = useTerms();
  const investigationModule = useClinicModule("investigation");
  const canUpdateAppt = useHasPermission("appointments:update");
  const canDeleteAppt = useHasPermission("appointments:delete");
  // read-all yoksa yalnızca kendi randevuları (sunucu da clinic-list'te zorlar)
  const canReadAll = useHasPermission("appointments:read-all");
  const myId = auth?._id ? String(auth._id) : null;

  /** Klinik çalışma takvimi */
  const clinicSchedule = auth?.clinic?.workingSchedule || auth?.user?.clinic?.workingSchedule || [];

  const bootRef = useRef(false);
  const fetchSeqRef = useRef(0);
  const scrollRef = useRef(null);
  // Izgara yeniden oluşturulduğunda ölçüm yeni kutuya bağlansın
  const [scrollNode, setScrollNode] = useState(null);
  const setScrollEl = useCallback((el) => {
    scrollRef.current = el;
    setScrollNode(el);
  }, []);
  const [scrollHeight, setScrollHeight] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [doctors, setDoctors] = useState([]);
  const [doctorsLoaded, setDoctorsLoaded] = useState(false);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [labTestDraft, setLabTestDraft] = useState([]);

  const [view, setView] = useState("day"); // day | week | list (Genel)
  // Genel listesini randevu değişikliklerinden sonra yeniden çektirmek için
  const [listReloadKey, setListReloadKey] = useState(0);
  const [scopePref, setScopePref] = useState(() => {
    try {
      return window.localStorage.getItem(SCOPE_STORAGE_KEY);
    } catch (_) {
      return null;
    }
  });
  // mine | all — tercih yoksa yetkiye göre; read-all yoksa her zaman mine
  const scope = !canReadAll ? "mine" : scopePref === "mine" ? "mine" : "all";
  const changeScope = (v) => {
    if (!v) return;
    setScopePref(v);
    try {
      window.localStorage.setItem(SCOPE_STORAGE_KEY, v);
    } catch (_) {
      // gizli pencere vb. — tercih yalnızca bu oturumda kalır
    }
  };
  const [cursorDate, setCursorDate] = useState(() => new Date());

  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [editComplaint, setEditComplaint] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [form, setForm] = useState({
    doctorId: "",
    patientId: "",
    date: "",
    startTime: "",
    endTime: "",
    notes: "",
    complaint: "",
    appointmentType: "ilk_muayene",
    requestLabTest: false,
    labTestIds: [],
    serviceId: "",
    patientPackageId: "",
  });

  const [listClinicDoctors, { isLoading: isDoctorsLoading }] =
    useListClinicDoctorsMutation();
  const [listPatients, { isLoading: isPatientsLoading }] =
    useListPatientsMutation();
  const [listClinicAppointments, { isLoading: isClinicApptLoading }] =
    useListClinicAppointmentsMutation();
  const [listDoctorAppointments, { isLoading: isMyApptLoading }] =
    useListDoctorAppointmentsMutation();
  const isApptLoading = isClinicApptLoading || isMyApptLoading;
  const [createAppointment, { isLoading: isCreateLoading }] =
    useCreateAppointmentMutation();
  const [cancelAppointment, { isLoading: isCancelLoading }] =
    useCancelAppointmentMutation();
  const [deleteAppointment, { isLoading: isDeleteLoading }] =
    useDeleteAppointmentMutation();
  const [updateAppointmentLabTests, { isLoading: isUpdateLabLoading }] =
    useUpdateAppointmentLabTestsMutation();
  const [listLabTests] = useListLabTestsMutation();
  const [updateAppointmentDetails, { isLoading: isUpdateDetailsLoading }] =
    useUpdateAppointmentDetailsMutation();

  /** Görüntülenen gün için çalışma saatlerini hesapla */
  const daySchedule = useMemo(() => {
    const dayIndex = cursorDate.getDay(); // 0=Pazar … 6=Cumartesi
    const dayName = DAY_INDEX_TO_TR[dayIndex];
    const entry = clinicSchedule.find((s) => s.day === dayName);
    if (entry) {
      const sh = parseHour(entry.startTime);
      const eh = parseHour(entry.endTime);
      if (sh !== null && eh !== null && eh > sh) {
        return { startHour: sh, endHour: eh, isClosed: false, dayName };
      }
    }
    // Çalışma takvimi yoksa veya bu gün kapalıysa varsayılan
    if (clinicSchedule.length > 0) {
      return { startHour: 9, endHour: 18, isClosed: true, dayName };
    }
    return { startHour: 8, endHour: 18, isClosed: false, dayName };
  }, [cursorDate, clinicSchedule]);

  const startHour = daySchedule.startHour;
  const endHour = daySchedule.endHour;
  const totalMinutes = (endHour - startHour) * 60;
  const gridHeight = useMemo(() => {
    // Kabı doldur; saat çoksa saat başına MIN_HOUR_PX altına inme, kap kayar
    const minHeight = (endHour - startHour) * MIN_HOUR_PX;
    if (scrollHeight && scrollHeight > 0) return Math.max(scrollHeight, minHeight);
    return (endHour - startHour) * 80;
  }, [scrollHeight, startHour, endHour]);
  const pxPerMinute = useMemo(
    () => (totalMinutes > 0 ? gridHeight / totalMinutes : 1),
    [gridHeight, totalMinutes]
  );

  /** Hasta aynı gün başka bir randevusu var mı? */
  const patientDayConflict = useMemo(() => {
    if (!form.patientId || !form.date) return null;
    const dayStart = new Date(form.date + "T00:00:00");
    const dayEnd = new Date(form.date + "T23:59:59");
    const existing = appointments.find((a) => {
      if (a.status === "cancelled") return false;
      const pid = String(a.patient?._id || a.patient || "");
      if (pid !== String(form.patientId)) return false;
      const s = new Date(a.startAt);
      return s >= dayStart && s <= dayEnd;
    });
    if (!existing) return null;
    return {
      startTime: toTimeStr(existing.startAt),
      endTime: toTimeStr(existing.endAt),
      doctorName: existing.snapshot?.doctorName || terms.doctor,
    };
  }, [form.patientId, form.date, appointments, terms]);

  const canCreate = useMemo(() => {
    if (!form.doctorId || !form.patientId) return false;
    if (!form.date || !form.startTime || !form.endTime) return false;
    if (form.endTime <= form.startTime) return false;
    // Minimum 15dk, maksimum 2 saat kontrol
    const [sh, sm] = form.startTime.split(":").map(Number);
    const [eh, em] = form.endTime.split(":").map(Number);
    const diffMin = (eh * 60 + em) - (sh * 60 + sm);
    if (diffMin < 15 || diffMin > 120) return false;
    // Hasta aynı gün zaten randevu almışsa engelle
    if (patientDayConflict) return false;
    return true;
  }, [form, patientDayConflict]);

  const selectedDoctor = useMemo(
    () => doctors.find((d) => String(d._id) === String(form.doctorId)) || null,
    [doctors, form.doctorId]
  );

  const selectedPatient = useMemo(
    () =>
      patients.find((p) => String(p._id) === String(form.patientId)) || null,
    [patients, form.patientId]
  );

  const patientById = useMemo(() => {
    const map = new Map();
    patients.forEach((p) => map.set(String(p._id), p));
    return map;
  }, [patients]);

  const selectedAppointmentTests = useMemo(() => {
    const ids = selectedAppointment?.labTestIds || [];
    if (!Array.isArray(ids) || ids.length === 0) return [];
    return ids
      .map((id) => labTests.find((t) => String(t._id) === String(id)))
      .filter(Boolean);
  }, [selectedAppointment, labTests]);

  const canEditLabTests = useMemo(() => {
    if (!selectedAppointment?.startAt) return false;
    return selectedAppointment.status === "scheduled";
  }, [selectedAppointment]);

  /** Seçili tarihin çalışma takvimi bilgisi */
  const dateSchedule = useMemo(
    () => getScheduleForDate(form.date, clinicSchedule),
    [form.date, clinicSchedule]
  );

  /**
   * Seçili doktor + tarih için o güne ait aktif randevuları bul.
   * Her randevunun başlangıç ve bitiş saatini "HH:mm" formatında hesapla.
   */
  const doctorDayAppointments = useMemo(() => {
    if (!form.doctorId || !form.date) return [];
    const dayStart = new Date(form.date + "T00:00:00");
    const dayEnd = new Date(form.date + "T23:59:59");
    return appointments
      .filter((a) => {
        if (String(a.doctor) !== String(form.doctorId)) return false;
        if (a.status === "cancelled") return false;
        const s = new Date(a.startAt);
        return s >= dayStart && s <= dayEnd;
      })
      .map((a) => ({
        start: toTimeStr(a.startAt),
        end: toTimeStr(a.endAt),
      }))
      .sort((a, b) => (a.start < b.start ? -1 : 1));
  }, [form.doctorId, form.date, appointments]);

  /**
   * Başlangıç saati için dolu (çakışan) slotları hesapla.
   * Mevcut randevuların kapladığı zaman aralıklarını bloke et.
   */
  const startBlockedSlots = useMemo(() => {
    const blocked = new Set();
    doctorDayAppointments.forEach(({ start, end }) => {
      // start (dahil) ile end (hariç) arasındaki tüm 15dk slotları bloke et
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      let m = sh * 60 + sm;
      const endM = eh * 60 + em;
      while (m < endM) {
        blocked.add(`${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`);
        m += 15;
      }
    });
    return [...blocked];
  }, [doctorDayAppointments]);

  /**
   * Bitiş saati sınırları:
   * - min: startTime + 15dk
   * - max: startTime + 2 saat, VEYA bir sonraki randevunun başlangıcı (hangisi küçükse)
   */
  const endTimeConstraints = useMemo(() => {
    if (!form.startTime) return { minTime: null, maxTime: null, blockedSlots: [], infoText: "" };

    const [sh, sm] = form.startTime.split(":").map(Number);
    const startMin = sh * 60 + sm;

    // Minimum: +15dk
    const minMin = startMin + 15;
    const minTime = `${pad2(Math.floor(minMin / 60))}:${pad2(minMin % 60)}`;

    // Maksimum: +2 saat
    const maxAbsolute = startMin + 120;
    let maxMin = maxAbsolute;

    // Eğer bu aralıkta başka randevu varsa, ona kadar sınırla
    let nextApptInfo = "";
    for (const appt of doctorDayAppointments) {
      const [ah, am] = appt.start.split(":").map(Number);
      const apptStartMin = ah * 60 + am;
      // Randevu başlangıç saatimizden sonra mı?
      if (apptStartMin > startMin && apptStartMin < maxMin) {
        maxMin = apptStartMin;
        nextApptInfo = `Sonraki randevu: ${appt.start}`;
        break;
      }
    }

    const maxTime = `${pad2(Math.floor(maxMin / 60) % 24)}:${pad2(maxMin % 60)}`;

    return { minTime, maxTime, blockedSlots: [], infoText: nextApptInfo };
  }, [form.startTime, doctorDayAppointments]);

  useEffect(() => {
    setLabTestDraft(selectedAppointmentTests);
  }, [selectedAppointmentTests]);

  useEffect(() => {
    if (selectedAppointment) {
      setEditComplaint(selectedAppointment.complaint || "");
      setEditNotes(selectedAppointment.notes || "");
    }
  }, [selectedAppointment]);

  const fetchDoctorsPatients = async () => {
    if (!token) return;
    setErrorMsg("");
    try {
      const [d, labs] = await Promise.all([
        listClinicDoctors({ token }).unwrap(),
        listLabTests({ token, status: "active", page: 1, limit: 500 }).unwrap(),
      ]);
      setDoctors(d?.data?.doctors || []);
      setLabTests(labs?.data?.items || []);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Veriler alınamadı.");
    } finally {
      setDoctorsLoaded(true);
    }
  };

  useEffect(() => {
    if (!token) return;
    const handle = setTimeout(async () => {
      try {
        const p = await listPatients({
          token,
          query: patientSearch,
          page: 1,
          limit: 50,
        }).unwrap();
        setPatients(p?.data?.patients || []);
      } catch (_) {
        // Arama sırasında sessizce geç
      }
    }, 350);

    return () => clearTimeout(handle);
  }, [token, patientSearch, listPatients]);

  const fetchAppointments = async () => {
    if (!token) return;
    // Genel görünüm kendi aramasını yapar; burada yalnız yenilemesini tetikle.
    if (view === "list") {
      setListReloadKey((k) => k + 1);
      return;
    }
    setErrorMsg("");
    try {
      let startAt;
      let endAt;
      if (view === "day") {
        startAt = startOfDay(cursorDate).toISOString();
        endAt = endOfDay(cursorDate).toISOString();
      } else {
        const start = startOfDay(cursorDate);
        const dow = start.getDay(); // 0 Sun
        const diff = (dow + 6) % 7; // Monday=0
        start.setDate(start.getDate() - diff);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        startAt = startOfDay(start).toISOString();
        endAt = endOfDay(end).toISOString();
      }

      const requestId = ++fetchSeqRef.current;
      const listFn = scope === "mine" ? listDoctorAppointments : listClinicAppointments;
      const a = await listFn({ token, startAt, endAt }).unwrap();
      // Hızlı tarih/kapsam değişiminde geç dönen eski yanıt ekranı ezmesin
      if (requestId !== fetchSeqRef.current) return;
      setAppointments(a?.data?.appointments || []);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Randevular alınamadı.");
    }
  };

  // Measure scroll container height so grid fills the column
  useLayoutEffect(() => {
    let rafId = 0;
    const updateHeight = () => {
      if (scrollRef.current) {
        setScrollHeight(scrollRef.current.clientHeight);
      }
    };
    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateHeight);
    };

    schedule();
    window.addEventListener("resize", schedule);
    const resizeObserver = new ResizeObserver(schedule);
    if (scrollNode) {
      resizeObserver.observe(scrollNode);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", schedule);
      resizeObserver.disconnect();
    };
  }, [view, scrollNode]);

  useEffect(() => {
    if (!token) return;
    if (bootRef.current) return;
    bootRef.current = true;
    fetchDoctorsPatients();
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cursorDate, scope]);

  const openCreate = () => {
    const now = new Date();
    const dateStr = toDateStr(now);
    const schedule = getScheduleForDate(dateStr, clinicSchedule);

    // Çalışma saatlerini al (yoksa default 08:00-18:00)
    const wStart = schedule && !schedule.isClosed && schedule.startTime
      ? schedule.startTime
      : "08:00";
    const wEnd = schedule && !schedule.isClosed && schedule.endTime
      ? schedule.endTime
      : "18:00";

    const nowTime = toTimeStr(now);
    let sTime = nowTime > wStart ? nowTime : wStart;
    // En yakın 15dk'ya yuvarla
    const [sh, sm] = sTime.split(":").map(Number);
    const rounded = Math.ceil((sh * 60 + sm) / 15) * 15;
    sTime = `${pad2(Math.floor(rounded / 60) % 24)}:${pad2(rounded % 60)}`;

    // Çalışma saati aşılmışsa başlangıca dön
    if (sTime > wEnd) sTime = wStart;

    setForm({
      doctorId: scope === "mine" && isMeDoctor ? myId : "",
      patientId: "",
      date: dateStr,
      startTime: sTime,
      endTime: "",
      notes: "",
      complaint: "",
      appointmentType: "ilk_muayene",
      requestLabTest: false,
      labTestIds: [],
      serviceId: "",
      patientPackageId: "",
    });
    setCreateOpen(true);
  };

  const openCreateAt = ({ doctorId, startAt, endAt }) => {
    setForm((p) => ({
      ...p,
      doctorId: doctorId || "",
      date: startAt ? toDateStr(startAt) : p.date,
      startTime: startAt ? toTimeStr(startAt) : p.startTime,
      endTime: endAt ? toTimeStr(endAt) : p.endTime,
    }));
    setCreateOpen(true);
  };

  const onCreate = async () => {
    if (!token) return;
    setErrorMsg("");
    try {
      await createAppointment({
        token,
        doctorId: form.doctorId,
        patientId: form.patientId,
        startAt: new Date(combineDatetime(form.date, form.startTime)).toISOString(),
        endAt: new Date(combineDatetime(form.date, form.endTime)).toISOString(),
        notes: form.notes || undefined,
        complaint: form.complaint || undefined,
        appointmentType: form.appointmentType || undefined,
        serviceId: form.serviceId || undefined,
        patientPackageId: form.patientPackageId || undefined,
        requestLabTest: form.requestLabTest || false,
        labTestIds:
          form.requestLabTest && Array.isArray(form.labTestIds)
            ? form.labTestIds.map((lt) => lt._id || lt)
            : [],
      }).unwrap();
      setCreateOpen(false);
      await fetchAppointments();
    } catch (e) {
      setErrorMsg(e?.data?.message || "Randevu oluşturulamadı.");
    }
  };

  // İptal modal'ını aç
  const openCancelModal = (apptId) => {
    setCancelTargetId(apptId);
    setCancelModalOpen(true);
  };

  // İptal modal'ından onaylama
  // PromptDialog kırpılmış (trim) gerekçeyle çağırır.
  const confirmCancel = async (reason = "") => {
    if (!token || !cancelTargetId) return;
    setErrorMsg("");
    try {
      await cancelAppointment({
        token,
        appointmentId: cancelTargetId,
        reason: reason || "Belirtilmedi",
      }).unwrap();
      setCancelModalOpen(false);
      setCancelTargetId(null);
      // Detay drawer açıksa kapat
      if (detailOpen) {
        setDetailOpen(false);
        setSelectedAppointment(null);
      }
      await fetchAppointments();
    } catch (e) {
      setErrorMsg(e?.data?.message || "Randevu iptal edilemedi.");
    }
  };

  // Silme modal'ını aç
  const openDeleteModal = (apptId) => {
    setDeleteTargetId(apptId);
    setDeleteModalOpen(true);
  };

  // Silme modal'ından onaylama
  const confirmDelete = async () => {
    if (!token || !deleteTargetId) return;
    setErrorMsg("");
    try {
      await deleteAppointment({
        token,
        appointmentId: deleteTargetId,
      }).unwrap();
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      if (detailOpen) {
        setDetailOpen(false);
        setSelectedAppointment(null);
      }
      await fetchAppointments();
    } catch (e) {
      setErrorMsg(e?.data?.message || "Randevu silinemedi.");
    }
  };

  const onUpdateLabTests = async () => {
    if (!token || !selectedAppointment) return;
    setErrorMsg("");
    try {
      const ids = (labTestDraft || [])
        .map((t) => t?._id || t)
        .filter(Boolean);
      const res = await updateAppointmentLabTests({
        token,
        appointmentId: selectedAppointment._id,
        labTestIds: ids,
      }).unwrap();
      const updated = res?.data?.appointment;
      if (updated) setSelectedAppointment(updated);
      await fetchAppointments();
    } catch (e) {
      setErrorMsg(
        e?.data?.message || "Laboratuvar testleri güncellenemedi."
      );
    }
  };

  const onUpdateDetails = async () => {
    if (!token || !selectedAppointment) return;
    setErrorMsg("");
    try {
      // Detayları güncelle (şikayet + not)
      const res = await updateAppointmentDetails({
        token,
        appointmentId: selectedAppointment._id,
        complaint: editComplaint,
        notes: editNotes,
      }).unwrap();

      // Tahlilleri güncelle
      const ids = (labTestDraft || [])
        .map((t) => t?._id || t)
        .filter(Boolean);
      const labRes = await updateAppointmentLabTests({
        token,
        appointmentId: selectedAppointment._id,
        labTestIds: ids,
      }).unwrap();

      const updated = labRes?.data?.appointment || res?.data?.appointment;
      if (updated) setSelectedAppointment(updated);
      await fetchAppointments();
    } catch (e) {
      setErrorMsg(e?.data?.message || "Randevu güncellenemedi.");
    }
  };

  const totalAppointments = appointments.length;

  const isMeDoctor = Boolean(myId) && doctors.some((d) => String(d._id) === myId);
  // Gün görünümü sütunları: kendi görünümünde yalnızca giriş yapan doktor
  const singleColumn = scope === "mine";
  const visibleDoctors = useMemo(
    () => (scope === "mine" ? doctors.filter((d) => String(d._id) === myId) : doctors),
    [scope, doctors, myId]
  );

  const apptsByDoctor = useMemo(() => {
    const m = new Map();
    doctors.forEach((d) => m.set(String(d._id), []));
    appointments.forEach((a) => {
      const id = String(a.doctor || "");
      if (!m.has(id)) m.set(id, []);
      m.get(id).push(a);
    });
    for (const [k, v] of m.entries()) {
      v.sort(
        (x, y) => new Date(x.startAt).getTime() - new Date(y.startAt).getTime()
      );
      m.set(k, v);
    }
    return m;
  }, [appointments, doctors]);

  const timeLabels = useMemo(() => {
    const labels = [];
    for (let h = startHour; h < endHour; h += 1) labels.push(h);
    return labels;
  }, [startHour, endHour]);

  // Kırmızı çizgi kaldırıldı - artık şu anki zaman çizgisi gösterilmiyor

  const onPrev = () => {
    setCursorDate((d) => {
      const x = new Date(d);
      x.setDate(x.getDate() + (view === "day" ? -1 : -7));
      return x;
    });
  };
  const onNext = () => {
    setCursorDate((d) => {
      const x = new Date(d);
      x.setDate(x.getDate() + (view === "day" ? 1 : 7));
      return x;
    });
  };
  const onToday = () => setCursorDate(new Date());

  const isPastDateTime = (date) => {
    const now = new Date();
    return date.getTime() < now.getTime();
  };

  const getStatusLabel = (status) => {
    const s = String(status || "scheduled").toLowerCase();
    const map = {
      scheduled: "Planlandı",
      in_examination: terms.inExam,
      cancelled: "İptal Edildi",
      completed: "Tamamlandı",
      no_show: "Gelmedi",
      finished: "Tamamlandı",
      encounter: "Görüşme",
    };
    return map[s] || "Planlandı";
  };

  const getAppointmentTypeStyle = (type) => {
    const t = String(type || "").toLowerCase();
    if (t === "ilk_muayene")
      return {
        bg: lighten(ACCENTS.cyan, 0.93),
        border: lighten(ACCENTS.cyan, 0.6),
        dot: ACCENTS.cyan,
        text: darken(ACCENTS.cyan, 0.5),
        icon: <FaStethoscope size={12} />,
        label: terms.firstVisitTitle,
      };
    if (t === "kontrol")
      return {
        bg: "#EFF6FF",
        border: "#93C5FD",
        dot: ACCENTS.blue,
        text: "#1E40AF",
        icon: <FaRedoAlt size={11} />,
        label: "Kontrol",
      };
    // Varsayılan (tip belirtilmemiş) → İlk Muayene olarak göster
    return {
      bg: lighten(ACCENTS.cyan, 0.93),
      border: lighten(ACCENTS.cyan, 0.6),
      dot: ACCENTS.cyan,
      text: darken(ACCENTS.cyan, 0.5),
      icon: <FaStethoscope size={12} />,
      label: terms.firstVisitTitle,
    };
  };

  const appointmentStyle = (a) => {
    const status = String(a.status || "scheduled").toLowerCase();

    // İptal edilmiş randevular için özel stil
    if (status === "cancelled")
      return {
        bg: COLORS.errorLightest,
        border: "#FECACA",
        dot: COLORS.error,
        text: "#991B1B",
        icon: <CloseIcon fontSize="small" />,
        label: "İptal",
      };

    // Tamamlanmış randevular için özel stil
    if (status === "finished" || status === "completed")
      return {
        bg: COLORS.successLight,
        border: "#86EFAC",
        dot: COLORS.successDark,
        text: "#14532D",
        icon: <FaCheckCircle size={14} />,
        label: "Tamamlandı",
      };

    // Gelmedi: muayenesi açılmadan bitişi geçen randevu — soluk gri
    if (status === "no_show")
      return {
        bg: COLORS.slate50,
        border: COLORS.slate200,
        dot: COLORS.slate400,
        text: COLORS.slate600,
        icon: <EventBusyIcon sx={{ fontSize: 14 }} />,
        label: "Gelmedi",
      };

    // Devam eden muayene için özel stil
    if (status === "in_examination")
      return {
        bg: lighten(IN_EXAM_TONE, 0.92),
        border: lighten(IN_EXAM_TONE, 0.6),
        dot: IN_EXAM_TONE,
        text: darken(IN_EXAM_TONE, 0.45),
        icon: <FaStethoscope size={14} />,
        label: terms.inExam,
      };

    // Randevu tipine göre stil
    return getAppointmentTypeStyle(a.appointmentType);
  };

  /**
   * Çakışan randevuları yan yana sütunlara yerleştir.
   * Her randevuya colIndex (sütun) ve totalCols (toplam sütun sayısı) ata.
   * minHeight etkisini de hesaba katarak görsel çakışmayı kontrol eder.
   */
  const layoutOverlaps = (apptList) => {
    if (!apptList || apptList.length === 0) return [];

    // Minimum kart yüksekliğinin dakika karşılığı
    const minCardMinutes = pxPerMinute > 0 ? Math.ceil(48 / pxPerMinute) : 15;

    // Her randevunun zaman aralığını hesapla (görsel genişliği ile)
    const items = apptList.map((a) => {
      const s = new Date(a.startAt).getTime();
      const e = new Date(a.endAt).getTime();
      const durationMs = e - s;
      const minDurationMs = minCardMinutes * 60 * 1000;
      // Görsel bitiş: gerçek bitiş veya minHeight'a denk gelen süre (hangisi büyükse)
      const visualEnd = Math.max(e, s + minDurationMs);
      return { appt: a, start: s, end: e, visualEnd, colIndex: 0, totalCols: 1 };
    });

    // Başlangıç saatine göre sırala
    items.sort((a, b) => a.start - b.start || a.end - b.end);

    // İki randevunun görsel olarak çakışıp çakışmadığını kontrol et
    const visuallyOverlaps = (a, b) =>
      a.start < b.visualEnd && a.visualEnd > b.start;

    // Sütun ata (greedy)
    const columns = []; // Her sütunun görsel bitiş zamanını tutar

    for (const item of items) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        // Bu sütundaki son randevuyla görsel çakışma var mı?
        if (columns[c] <= item.start) {
          item.colIndex = c;
          columns[c] = item.visualEnd;
          placed = true;
          break;
        }
      }
      if (!placed) {
        item.colIndex = columns.length;
        columns.push(item.visualEnd);
      }
    }

    // Overlap gruplarını belirle (BFS) ve her grubun toplam sütun sayısını ata
    const n = items.length;
    const visited = new Array(n).fill(false);

    for (let i = 0; i < n; i++) {
      if (visited[i]) continue;
      const group = [i];
      visited[i] = true;
      let qi = 0;
      while (qi < group.length) {
        const curr = group[qi++];
        for (let j = 0; j < n; j++) {
          if (visited[j]) continue;
          if (visuallyOverlaps(items[j], items[curr])) {
            visited[j] = true;
            group.push(j);
          }
        }
      }
      const maxCol = Math.max(...group.map((idx) => items[idx].colIndex)) + 1;
      group.forEach((idx) => { items[idx].totalCols = maxCol; });
    }

    return items;
  };

  const onGridClick = (e, doctorId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const scrollTop = scrollRef.current?.scrollTop || 0;
    const y = e.clientY - rect.top + scrollTop;
    const minute = clamp(
      Math.round(y / pxPerMinute / 15) * 15,
      0,
      totalMinutes
    );
    const start = new Date(cursorDate);
    start.setHours(startHour, 0, 0, 0);
    start.setMinutes(start.getMinutes() + minute);
    const end = new Date(start.getTime() + 30 * 60 * 1000);

    // Geçmiş tarih/saat kontrolü
    if (isPastDateTime(start)) {
      return;
    }

    if (!canCreateAppt) return;

    openCreateAt({ doctorId, startAt: start, endAt: end });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: COLORS.slate50,
        overflow: "hidden",
        // Muayenesi süren randevu animasyonları (kart halkası + rozet noktası)
        ...examLiveKeyframesSx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Üst başlık — başlık + tarih gezinme + görünüm + aksiyon */}
        <PageHeaderBar
          icon={<FaCalendarAlt size={18} />}
          title="Randevular"
          subtitle={
            <>
              {view === "list" ? (
                scope === "mine"
                  ? `Size atanan randevular · ${terms.patientLower}, tarih ve duruma göre arayın`
                  : `Tüm randevular · ${terms.patientLower}, tarih ve duruma göre arayın`
              ) : (
                <>
              {scope === "mine" ? "Size atanan " : ""}
              {totalAppointments} randevu
                </>
              )}
              {view === "day" && canCreateAppt && endOfDay(cursorDate) >= new Date()
                ? " · Boş saate tıklayarak randevu ekleyin"
                : ""}
            </>
          }
          action={
            <>
              {/* Orta: tarih gezinme (Genel'de yok — orada tarih aralığı filtresi var) */}
              {view !== "list" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                    p: 0.5,
                    borderRadius: 2.5,
                    bgcolor: COLORS.slate50,
                    border: `1px solid ${COLORS.slate200}`,
                  }}
                >
                  <IconButton
                    onClick={onPrev}
                    size="small"
                    aria-label="Önceki"
                    sx={{ width: 30, height: 30, borderRadius: 2, "&:hover": { bgcolor: COLORS.white } }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: COLORS.slate700,
                      minWidth: 170,
                      textAlign: "center",
                      px: 1,
                    }}
                  >
                    {view === "week" ? fmtWeekTitle(cursorDate) : fmtDayTitle(cursorDate)}
                  </Typography>
                  <IconButton
                    onClick={onNext}
                    size="small"
                    aria-label="Sonraki"
                    sx={{ width: 30, height: 30, borderRadius: 2, "&:hover": { bgcolor: COLORS.white } }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Button
                  onClick={onToday}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    px: 1.75,
                    color: palette.primary,
                    bgcolor: alpha(palette.primary, 0.08),
                    "&:hover": { bgcolor: alpha(palette.primary, 0.14) },
                  }}
                >
                  Bugün
                </Button>
                {daySchedule.isClosed && view === "day" && (
                  <Chip
                    label="Klinik kapalı"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: COLORS.errorLightest,
                      color: COLORS.errorDark,
                      border: `1px solid ${alpha(COLORS.error, 0.2)}`,
                      fontSize: 12,
                    }}
                  />
                )}
                {!daySchedule.isClosed && clinicSchedule.length > 0 && view === "day" && (
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: "15px !important" }} />}
                    label={`${daySchedule.startHour.toString().padStart(2, "0")}:00 – ${daySchedule.endHour.toString().padStart(2, "0")}:00`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(COLORS.success, 0.08),
                      color: COLORS.successDarker,
                      border: `1px solid ${alpha(COLORS.success, 0.2)}`,
                      fontSize: 12,
                      "& .MuiChip-icon": { color: COLORS.successDark },
                    }}
                  />
                )}
              </Box>

              )}

              {/* Sağ: kapsam + görünüm + aksiyon */}
              {canReadAll && (
                <SegmentedControl
                  value={scope}
                  onChange={changeScope}
                  ariaLabel="Randevu kapsamı"
                  options={[
                    { value: "mine", label: "Randevularım" },
                    { value: "all", label: "Tüm Randevular" },
                  ]}
                />
              )}
              <SegmentedControl
                value={view}
                onChange={setView}
                ariaLabel="Takvim görünümü"
                options={[
                  { value: "day", label: "Gün" },
                  { value: "week", label: "Hafta" },
                  { value: "list", label: "Genel" },
                ]}
              />
              {canCreateAppt && (
                <ActionButton
                  variant="add"
                  palette={palette}
                  label="Randevu Ekle"
                  onClick={openCreate}
                />
              )}
            </>
          }
        />

        {/* Renk açıklaması — takvimdeki kutuların ne anlama geldiği */}
        {view === "day" && (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              px: { xs: 2, sm: 3 },
              py: 1,
              bgcolor: COLORS.white,
              borderBottom: `1px solid ${COLORS.slate100}`,
            }}
          >
            {[
              appointmentStyle({ appointmentType: "ilk_muayene" }),
              appointmentStyle({ appointmentType: "kontrol" }),
              appointmentStyle({ status: "in_examination" }),
              appointmentStyle({ status: "completed" }),
              appointmentStyle({ status: "no_show" }),
              appointmentStyle({ status: "cancelled" }),
            ].map((st) => (
              <Box key={st.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 12,
                    borderRadius: "3px",
                    bgcolor: st.bg,
                    border: `1px solid ${st.border}`,
                    borderLeft: `3px solid ${st.dot}`,
                    boxSizing: "border-box",
                  }}
                />
                <Typography sx={{ fontSize: "0.75rem", color: COLORS.slate600 }}>
                  {st.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {errorMsg ? (
          <Alert
            severity="error"
            sx={{ borderRadius: 0, flexShrink: 0 }}
            onClose={() => setErrorMsg("")}
          >
            {errorMsg}
          </Alert>
        ) : null}

        {/* Calendar Grid - Full Page */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            bgcolor: "white",
            overflow: "hidden",
          }}
        >
          {view === "list" ? (
            <AppointmentListView
              token={token}
              scope={scope}
              reloadKey={listReloadKey}
              canCreate={canCreateAppt}
              onCreate={openCreate}
              onOpen={(a) => {
                setSelectedAppointment(a);
                setDetailOpen(true);
              }}
            />
          ) : view === "week" ? (
            <Box
              sx={{
                p: { xs: 1.5, sm: 2.5 },
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                bgcolor: COLORS.slate50,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  flex: 1,
                  minHeight: 0,
                  // Kartlar küçülmesin: aksi halde kaydırma kabı yerine kartlar
                  // ezilip içerikleri görünmez oluyordu.
                  "& > *": { flexShrink: 0 },
                }}
              >
                {!isApptLoading && appointments.length === 0 ? (
                  <EmptyState
                    icon={<FaCalendarAlt />}
                    title={scope === "mine" ? "Bu hafta size atanmış randevu yok" : "Bu hafta randevu yok"}
                    description={
                      canCreateAppt
                        ? "Yeni randevu için sağ üstteki “Randevu Ekle” butonunu kullanın veya Gün görünümünde boş bir saate tıklayın."
                        : "Seçilen haftada planlanmış randevu bulunmuyor."
                    }
                    sx={{ flex: 1, "& svg": { color: COLORS.slate300 } }}
                  />
                ) : null}
                {isApptLoading
                  ? [...Array(8)].map((_, i) => (
                      <Card key={i} elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${COLORS.slate200}` }}>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                          <Skeleton variant="circular" width={44} height={44} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="50%" />
                            <Skeleton variant="text" width="30%" />
                          </Box>
                          <Skeleton variant="rounded" width={80} height={28} />
                        </Box>
                      </Card>
                    ))
                  : (() => {
                      const now = new Date();
                      // Sırala: en yakın başlangıç zamanına göre (geçmişte kalanlar sona)
                      const sorted = [...appointments].sort((a, b) => {
                        const aStart = new Date(a.startAt);
                        const bStart = new Date(b.startAt);
                        const aEnd = new Date(a.endAt);
                        const bEnd = new Date(b.endAt);
                        const aStatus = String(a.status || "scheduled").toLowerCase();
                        const bStatus = String(b.status || "scheduled").toLowerCase();
                        const aIsCancelled = aStatus === "cancelled";
                        const bIsCancelled = bStatus === "cancelled";
                        // Devam eden en üstte
                        const aOngoing = aStart <= now && aEnd > now && !aIsCancelled;
                        const bOngoing = bStart <= now && bEnd > now && !bIsCancelled;
                        if (aOngoing && !bOngoing) return -1;
                        if (!aOngoing && bOngoing) return 1;
                        // Gelecek > geçmiş
                        const aFuture = aStart > now && !aIsCancelled;
                        const bFuture = bStart > now && !bIsCancelled;
                        if (aFuture && !bFuture) return -1;
                        if (!aFuture && bFuture) return 1;
                        // İptal edilen en sona
                        if (aIsCancelled && !bIsCancelled) return 1;
                        if (!aIsCancelled && bIsCancelled) return -1;
                        return aStart - bStart;
                      });

                      return sorted.map((a) => {
                        const aStart = new Date(a.startAt);
                        const aEnd = new Date(a.endAt);
                        const status = String(a.status || "scheduled").toLowerCase();
                        const isCancelled = status === "cancelled";
                        const isCompleted = status === "finished" || status === "completed";
                        const isInExam = status === "in_examination";
                        const isNoShow = status === "no_show";
                        const isOngoing = aStart <= now && aEnd > now && !isCancelled && !isCompleted;
                        const isFuture = aStart > now && !isCancelled;
                        const isPast = aEnd <= now && !isCancelled && !isCompleted && !isNoShow;
                        const typeStyle = getAppointmentTypeStyle(a.appointmentType);

                        // Kalan süre hesapla
                        let timeInfo = "";
                        let timeColor = "text.secondary";
                        if (isOngoing) {
                          const diffMs = aEnd - now;
                          const diffMin = Math.ceil(diffMs / 60000);
                          timeInfo = diffMin > 60
                            ? `${Math.floor(diffMin / 60)} sa ${diffMin % 60} dk kaldı`
                            : `${diffMin} dk kaldı`;
                          timeColor = COLORS.successDark;
                        } else if (isFuture) {
                          const diffMs = aStart - now;
                          const diffMin = Math.ceil(diffMs / 60000);
                          if (diffMin < 60) {
                            timeInfo = `${diffMin} dk sonra`;
                          } else if (diffMin < 1440) {
                            timeInfo = `${Math.floor(diffMin / 60)} sa ${diffMin % 60} dk sonra`;
                          } else {
                            const days = Math.floor(diffMin / 1440);
                            timeInfo = `${days} gün sonra`;
                          }
                          timeColor = ACCENTS.blue;
                        } else if (isPast) {
                          timeInfo = "Tamamlanmadı";
                          timeColor = COLORS.warning;
                        }
                        // Tamamlandı / İptal zaten sol durum şeridinde yazıyor — tekrar etme

                        // Durum tonu — kart beyaz kalır, renk sol şerit + rozetle anlatılır
                        let statusTone;
                        if (isInExam) statusTone = IN_EXAM_TONE;
                        else if (isNoShow) statusTone = COLORS.slate400;
                        else if (isCancelled) statusTone = COLORS.error;
                        else if (isOngoing || isCompleted) statusTone = COLORS.success;
                        else if (isFuture) statusTone = ACCENTS.blue;
                        else statusTone = COLORS.warning;
                        const statusText = isInExam
                          ? darken(IN_EXAM_TONE, 0.35)
                          : isNoShow
                          ? COLORS.slate600
                          : isCancelled
                          ? COLORS.errorDark
                          : isOngoing || isCompleted
                            ? COLORS.successDark
                            : isFuture
                              ? ACCENTS.blue
                              : COLORS.warningDark;
                        const statusLabel = isInExam
                          ? terms.inExam
                          : isNoShow
                          ? "Gelmedi"
                          : isOngoing
                          ? "Devam ediyor"
                          : isFuture
                            ? "Yaklaşan"
                            : isCancelled
                              ? "İptal"
                              : isCompleted
                                ? "Tamamlandı"
                                : "Geçmiş";

                        // Sol ikon
                        let statusIcon;
                        if (isInExam) {
                          statusIcon = <FaStethoscope size={18} color={IN_EXAM_TONE} />;
                        } else if (isNoShow) {
                          statusIcon = <EventBusyIcon sx={{ fontSize: 20, color: COLORS.slate400 }} />;
                        } else if (isOngoing) {
                          statusIcon = <HourglassIcon sx={{ fontSize: 20, color: COLORS.successDark }} />;
                        } else if (isFuture) {
                          statusIcon = <ScheduleIcon sx={{ fontSize: 20, color: ACCENTS.blue }} />;
                        } else if (isCancelled) {
                          statusIcon = <EventBusyIcon sx={{ fontSize: 20, color: COLORS.error }} />;
                        } else if (isCompleted) {
                          statusIcon = <FaCheckCircle size={18} color={COLORS.successDark} />;
                        } else {
                          statusIcon = <AccessTimeIcon sx={{ fontSize: 20, color: COLORS.warning }} />;
                        }

                        return (
                          <Card
                            key={a._id}
                            className={isInExam ? "exam-live" : undefined}
                            onClick={() => {
                              setSelectedAppointment(a);
                              setDetailOpen(true);
                            }}
                            sx={{
                              p: 0,
                              borderRadius: 3,
                              border: `1px solid ${COLORS.slate200}`,
                              borderLeft: `4px solid ${statusTone}`,
                              bgcolor: isCancelled ? COLORS.slate50 : COLORS.white,
                              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                              cursor: "pointer",
                              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                              overflow: "hidden",
                              ...(isInExam && {
                                animation: "examRing 2s ease-out infinite",
                              }),
                              "&:hover": {
                                borderColor: alpha(statusTone, 0.45),
                                borderLeftColor: statusTone,
                                boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "stretch" }}>
                              {/* Sol renk şeridi + ikon */}
                              <Box
                                sx={{
                                  width: 84,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: alpha(statusTone, 0.06),
                                  flexShrink: 0,
                                  gap: 0.5,
                                  py: 1.5,
                                  px: 0.5,
                                }}
                              >
                                {statusIcon}
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    color: statusText,
                                    textAlign: "center",
                                    lineHeight: 1.15,
                                    px: 0.5,
                                  }}
                                >
                                  {statusLabel}
                                </Typography>
                              </Box>

                              {/* Ana içerik */}
                              <Box sx={{ flex: 1, p: 1.5, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                                {/* Satır 1: Hasta adı + tip (sol) — durum + süre + buton (sağ) */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
                                  {/* Sol: hasta + tip */}
                                  <PersonIcon sx={{ fontSize: 18, color: typeStyle.text, opacity: 0.8, flexShrink: 0 }} />
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.92rem",
                                      color: isCancelled ? COLORS.slate500 : COLORS.slate800,
                                      textDecoration: isCancelled ? "line-through" : "none",
                                    }}
                                    noWrap
                                  >
                                    {a?.snapshot?.patientName || terms.patient}
                                  </Typography>
                                  <Chip
                                    label={typeStyle.label}
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.68rem",
                                      height: 22,
                                      bgcolor: typeStyle.bg,
                                      color: typeStyle.text,
                                      border: `1px solid ${typeStyle.border}`,
                                      flexShrink: 0,
                                    }}
                                  />

                                  {/* Boşluk */}
                                  <Box sx={{ flex: 1 }} />

                                  {/* Sağ: kalan süre */}
                                  {timeInfo && (
                                    <Typography
                                      sx={{
                                        fontSize: "0.78rem",
                                        fontWeight: 600,
                                        color: timeColor,
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {timeInfo}
                                    </Typography>
                                  )}
                                </Box>

                                {/* Satır 2: Doktor + saat + şikayet + tahlil - yatay yayılmış */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                                  {/* Doktor — rol etiketi + title + ad soyad */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                    <FaUserMd size={13} color={COLORS.slate500} />
                                    <Chip
                                      label={a?.snapshot?.doctorRoleName || terms.doctor}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                        bgcolor: COLORS.slate100,
                                        color: COLORS.slate600,
                                        border: `1px solid ${COLORS.slate200}`,
                                        "& .MuiChip-label": { px: 0.75 },
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500, color: COLORS.slate600, fontSize: "0.82rem", minWidth: 0 }}
                                      noWrap
                                      title={
                                        `${a?.snapshot?.doctorTitle ? a.snapshot.doctorTitle + " " : ""}${a?.snapshot?.doctorName || ""}`.trim()
                                      }
                                    >
                                      {a?.snapshot?.doctorTitle ? `${a.snapshot.doctorTitle} ` : ""}
                                      {a?.snapshot?.doctorName || terms.doctor}
                                    </Typography>
                                  </Box>

                                  {/* Saat */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: 15, color: COLORS.slate500 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.slate600, fontSize: "0.82rem", fontVariantNumeric: "tabular-nums" }}>
                                      {aStart.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" })}{" "}
                                      {aStart.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} -{" "}
                                      {aEnd.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                                    </Typography>
                                  </Box>

                                  {/* Şikayet */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <MedicalInfoIcon sx={{ fontSize: 14, color: a.complaint ? COLORS.slate500 : COLORS.slate300, flexShrink: 0 }} />
                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{
                                        color: a.complaint ? COLORS.slate600 : COLORS.slate400,
                                        fontStyle: a.complaint ? "normal" : "italic",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {a.complaint || "Şikayet girilmedi"}
                                    </Typography>
                                  </Box>

                                  {/* Tahlil */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <BiotechIcon sx={{ fontSize: 14, color: a.requestLabTest ? ACCENTS.blue : COLORS.slate300, flexShrink: 0 }} />
                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{
                                        color: a.requestLabTest ? COLORS.slate600 : COLORS.slate400,
                                        fontStyle: a.requestLabTest ? "normal" : "italic",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {a.requestLabTest
                                        ? `${(a.labTestIds || []).length} tahlil istendi`
                                        : "Tahlil istenmedi"}
                                    </Typography>
                                  </Box>

                                  {/* İptal butonu - satır 2'nin sağında */}
                                  <Box sx={{ flex: 1 }} />
                                  {canUpdateAppt && a.status === "scheduled" && (
                                    <ActionButton
                                      variant="delete"
                                      tone="soft"
                                      compact
                                      label="İptal Et"
                                      icon={<EventBusyIcon sx={{ fontSize: 15 }} />}
                                      disabled={isCancelLoading}
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        openCancelModal(a._id);
                                      }}
                                    />
                                  )}
                                  {canDeleteAppt && isCancelled && (
                                    <ActionButton
                                      variant="delete"
                                      tone="soft"
                                      compact
                                      label="Sil"
                                      icon={<CloseIcon sx={{ fontSize: 15 }} />}
                                      disabled={isDeleteLoading}
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        openDeleteModal(a._id);
                                      }}
                                    />
                                  )}
                                </Box>

                                {/* İptal nedeni + tarih - aynı satır */}
                                {isCancelled && (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <EventBusyIcon sx={{ fontSize: 14, color: COLORS.error, flexShrink: 0 }} />
                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{
                                        color: COLORS.errorDark,
                                        fontWeight: 500,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {a.cancellation?.reason || "İptal nedeni belirtilmedi"}
                                    </Typography>
                                    {a.cancellation?.cancelledAt && (
                                      <>
                                        <Typography variant="caption" sx={{ color: COLORS.slate400, mx: 0.5 }}>•</Typography>
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{
                                            color: COLORS.slate400,
                                            fontSize: "0.72rem",
                                          }}
                                        >
                                          {new Date(a.cancellation.cancelledAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })}{" "}
                                          {new Date(a.cancellation.cancelledAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Card>
                        );
                      });
                    })(
                  )}
              </Box>
            </Box>
          ) : scope === "mine" && doctorsLoaded && !isDoctorsLoading && visibleDoctors.length === 0 ? (
            <EmptyState
              icon={<FaUserMd />}
              title="Size atanmış bir randevu takvimi yok"
              description={
                canReadAll
                  ? `“Randevularım” yalnızca ${terms.doctorLower} olarak atandığınız randevuları gösterir. Kliniğin tüm randevuları için “Tüm Randevular”a geçin.`
                  : `“Randevularım” yalnızca ${terms.doctorLower} olarak atandığınız randevuları gösterir. Diğer ${terms.doctorLower}ların randevularını görme yetkiniz yok.`
              }
              sx={{ flex: 1, bgcolor: COLORS.slate50, "& svg": { color: COLORS.slate300 } }}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              {/* Header Row: saat sütunu + doktorlar — Randevularım'da tek doktor, başlık gereksiz */}
              <Box
                sx={{
                  display: singleColumn ? "none" : "flex",
                  borderBottom: `1px solid ${COLORS.slate200}`,
                  bgcolor: "white",
                }}
              >
                <Box
                  sx={{
                    width: { xs: 70, sm: 80, md: 90 },
                    borderRight: `1px solid ${COLORS.slate200}`,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ display: "flex", minWidth: "max-content" }}>
                  {(isDoctorsLoading ? [...Array(scope === "mine" ? 1 : 3)] : visibleDoctors).map(
                    (d, idx) => {
                      if (!d) {
                        return (
                          <Box
                            key={idx}
                            sx={{
                              flex: "0 0 auto",
                              width: { xs: 250, sm: 300, md: 350 },
                              p: { xs: 1, sm: 1.5, md: 2 },
                              borderRight: `1px solid ${COLORS.slate200}`,
                              flexShrink: 0,
                            }}
                          >
                            <Skeleton variant="text" width="70%" />
                            <Skeleton variant="text" width="50%" />
                          </Box>
                        );
                      }
                      const did = String(d._id);
                      const dayCount = (apptsByDoctor.get(did) || []).length;
                      const initials = `${(d.name || "D").trim().charAt(0)}${(
                        d.surname || "X"
                      )
                        .trim()
                        .charAt(0)}`.toUpperCase();
                      return (
                        <Box
                          key={did}
                          sx={{
                            flex: "0 0 auto",
                            width: { xs: 250, sm: 300, md: 350 },
                            py: 1.25,
                            px: 2,
                            borderRight: `1px solid ${COLORS.slate100}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexShrink: 0,
                            bgcolor: COLORS.white,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: alpha(palette.primary, 0.08),
                              color: palette.primary,
                              border: `1px solid ${alpha(palette.primary, 0.16)}`,
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                              {staffRoleName(d) ? (
                                <Chip
                                  label={staffRoleName(d)}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.68rem",
                                    fontWeight: 600,
                                    bgcolor: COLORS.slate100,
                                    color: COLORS.slate600,
                                    border: `1px solid ${COLORS.slate200}`,
                                    flexShrink: 0,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              ) : null}
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  color: COLORS.slate800,
                                }}
                                noWrap
                              >
                                {staffFullName(d)}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: COLORS.slate500,
                                fontWeight: 500,
                                fontSize: 12,
                              }}
                            >
                              {dayCount === 0 ? "Randevu yok" : `${dayCount} randevu`}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                  )}
                </Box>
              </Box>

              {/* Body: ONE shared scroll container so time + grid move together */}
              <Box
                ref={setScrollEl}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowX: "hidden",
                  overflowY: "auto",
                  bgcolor: "white",
                  position: "relative",
                }}
              >
                {/* Kırmızı çizgi kaldırıldı */}

                <Box
                  sx={{
                    display: "flex",
                    minWidth: singleColumn ? "100%" : "max-content",
                    height: gridHeight,
                    minHeight: gridHeight,
                  }}
                >
                  {/* time gutter (scrolls together) */}
                  <Box
                    sx={{
                      width: { xs: 70, sm: 80, md: 90 },
                      borderRight: `1px solid ${COLORS.slate200}`,
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        height: gridHeight,
                        minHeight: gridHeight,
                      }}
                    >
                      {timeLabels.map((h) => {
                        const top = (h - startHour) * 60 * pxPerMinute;
                        const label = new Date(
                          2000,
                          0,
                          1,
                          h,
                          0
                        ).toLocaleTimeString("tr-TR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                        return (
                          <Box
                            key={h}
                            sx={{
                              position: "absolute",
                              top: top + 4,
                              left: 8,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: COLORS.slate400,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* columns */}
                  <Box
                    sx={{
                      display: "flex",
                      position: "relative",
                      minWidth: "max-content",
                      flex: singleColumn ? 1 : "0 0 auto",
                      height: gridHeight,
                      minHeight: gridHeight,
                    }}
                  >
                    {(visibleDoctors || []).map((d) => {
                      const did = String(d._id);
                      const list = apptsByDoctor.get(did) || [];

                      return (
                        <Box
                          key={did}
                          sx={{
                            flex: singleColumn ? 1 : "0 0 auto",
                            width: singleColumn ? "auto" : { xs: 250, sm: 300, md: 350 },
                            borderRight: singleColumn ? "none" : `1px solid ${COLORS.slate200}`,
                            position: "relative",
                            flexShrink: 0,
                            bgcolor: COLORS.white,
                          }}
                          onClick={(e) => onGridClick(e, did)}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              height: gridHeight,
                              minHeight: gridHeight,
                              width: "100%",
                            }}
                          >
                            {/* Hour slot boxes with individual hover effects */}
                            {Array.from(
                              { length: endHour - startHour },
                              (_, i) => {
                                const hour = startHour + i;
                                const slotTop = i * 60 * pxPerMinute;
                                const slotHeight = 60 * pxPerMinute;
                                const isFirst = i === 0;

                                // Geçmiş saat kontrolü
                                const checkDate = new Date(cursorDate);
                                checkDate.setHours(hour, 0, 0, 0);
                                const isPast = isPastDateTime(checkDate);

                                return (
                                  <Box
                                    key={`slot-${hour}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isPast) return;

                                      const rect =
                                        e.currentTarget.getBoundingClientRect();
                                      const y = e.clientY - rect.top;
                                      const minute = clamp(
                                        Math.round(
                                          (y / pxPerMinute) / 15
                                        ) * 15,
                                        0,
                                        60
                                      );
                                      const start = new Date(cursorDate);
                                      start.setHours(hour, minute, 0, 0);
                                      const end = new Date(
                                        start.getTime() + 30 * 60 * 1000
                                      );

                                      // Ek kontrol
                                      if (isPastDateTime(start)) return;

                                      if (!canCreateAppt) return;

                                      // Randevu olan saate tıklanamaz
                                      const clickMs = start.getTime();
                                      const isOccupied = list.some((appt) => {
                                        const as = new Date(appt.startAt).getTime();
                                        const ae = new Date(appt.endAt).getTime();
                                        return clickMs >= as && clickMs < ae;
                                      });
                                      if (isOccupied) return;

                                      openCreateAt({
                                        doctorId: did,
                                        startAt: start,
                                        endAt: end,
                                      });
                                    }}
                                    sx={{
                                      position: "absolute",
                                      left: 0,
                                      right: 0,
                                      top: slotTop,
                                      height: slotHeight,
                                      borderTop: isFirst
                                        ? `1px solid ${COLORS.slate100}`
                                        : "none",
                                      borderBottom: `1px solid ${COLORS.slate100}`,
                                      cursor: isPast
                                        ? "not-allowed"
                                        : "pointer",
                                      transition: "background-color 0.2s ease",
                                      zIndex: 1,
                                      backgroundColor: isPast
                                        ? COLORS.slate50
                                        : "white",
                                      backgroundImage: isPast
                                        ? `repeating-linear-gradient(135deg, transparent 0 8px, ${alpha(COLORS.slate200, 0.45)} 8px 9px)`
                                        : "none",
                                      "&:hover": isPast
                                        ? {}
                                        : {
                                            backgroundColor: alpha(palette.primary, 0.05),
                                          },
                                    }}
                                  />
                                );
                              }
                            )}

                            {isApptLoading ? (
                              <Box
                                sx={{ p: 2, position: "relative", zIndex: 2 }}
                              >
                                <Skeleton variant="rounded" height={88} />
                                <Box sx={{ mt: 2 }}>
                                  <Skeleton variant="rounded" height={88} />
                                </Box>
                              </Box>
                            ) : (
                              layoutOverlaps(list).map(({ appt: a, colIndex, totalCols }) => {
                                const s = new Date(a.startAt);
                                const e = new Date(a.endAt);
                                const startMin =
                                  s.getHours() * 60 +
                                  s.getMinutes() -
                                  startHour * 60;
                                const endMin =
                                  e.getHours() * 60 +
                                  e.getMinutes() -
                                  startHour * 60;
                                const top = startMin * pxPerMinute;
                                const durationPx = (endMin - startMin) * pxPerMinute;
                                const style = appointmentStyle(a);
                                const status = String(
                                  a.status || "scheduled"
                                ).toLowerCase();
                                const isCancelled = status === "cancelled";
                                const isInExam = status === "in_examination";
                                const patientId =
                                  typeof a.patient === "object"
                                    ? a.patient?._id
                                    : a.patient;
                                const patientFallback = patientId
                                  ? patientById.get(String(patientId))
                                  : null;
                                const patientName =
                                  a?.snapshot?.patientName ||
                                  `${patientFallback?.name || ""} ${
                                    patientFallback?.surname || ""
                                  }`.trim() ||
                                  terms.patient;
                                const typeLabel = getAppointmentTypeStyle(
                                  a.appointmentType
                                ).label;

                                const hasOverlap = totalCols > 1;

                                // Konum ve boyut hesapla – her zaman sütun bazlı
                                let cardLeft, cardWidth, cardZIndex, cardPadding;

                                if (hasOverlap) {
                                  const colWidthPct = 100 / totalCols;
                                  const colLeftPct = colIndex * colWidthPct;
                                  const gap = 2;
                                  cardLeft = `calc(${colLeftPct}% + ${gap}px)`;
                                  cardWidth = `calc(${colWidthPct}% - ${gap * 2}px)`;
                                  cardZIndex = 2 + colIndex;
                                  cardPadding = 0.8;
                                } else {
                                  cardLeft = 4;
                                  cardWidth = "calc(100% - 8px)";
                                  cardZIndex = 2;
                                  cardPadding = 1.5;
                                }

                                return (
                                  <React.Fragment key={a._id}>
                                  {/* Süre göstergesi – kompakt kartın altına uzanan çizgi */}
                                  {durationPx > 52 && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: cardLeft,
                                        top,
                                        height: durationPx,
                                        width: 3,
                                        bgcolor: style.border,
                                        opacity: 0.22,
                                        borderRadius: 1,
                                        zIndex: 1,
                                        pointerEvents: "none",
                                      }}
                                    />
                                  )}
                                  <Box
                                    className={isInExam ? "exam-live" : undefined}
                                    onClick={(evt) => {
                                      evt.stopPropagation();
                                      setSelectedAppointment(a);
                                      setDetailOpen(true);
                                    }}
                                    sx={{
                                      ...(isInExam && {
                                        animation: "examRing 2s ease-out infinite",
                                      }),
                                      position: "absolute",
                                      left: cardLeft,
                                      width: cardWidth,
                                      top,
                                      height: "auto",
                                      minHeight: 48,
                                      background: style.bg,
                                      border: `1px solid ${style.border}`,
                                      borderLeft: `3px solid ${style.dot}`,
                                      borderRadius: 2,
                                      p: cardPadding,
                                      boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
                                      display: "flex",
                                      flexDirection: "column",
                                      cursor: "pointer",
                                      zIndex: cardZIndex,
                                      transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
                                      overflow: "hidden",
                                      "& .appt-content": {
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: 1,
                                        overflow: "hidden",
                                      },
                                      // Hover'da gizlenen detaylar
                                      "& .appt-hover-detail": {
                                        maxHeight: 0,
                                        opacity: 0,
                                        overflow: "hidden",
                                        transition: "max-height 0.25s ease, opacity 0.2s ease",
                                      },
                                      "& .appt-complaint": {
                                        display: "-webkit-box",
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        transition: "all 0.2s ease",
                                      },
                                      "&:hover": {
                                        left: 4,
                                        width: "calc(100% - 8px)",
                                        height: "auto",
                                        zIndex: 30,
                                        boxShadow: "0 10px 28px rgba(15,23,42,0.14)",
                                        p: 1.5,
                                        "& .appt-hover-detail": {
                                          maxHeight: 200,
                                          opacity: 1,
                                        },
                                        "& .appt-complaint": {
                                          WebkitLineClamp: "unset",
                                          display: "block",
                                          overflow: "visible",
                                        },
                                      },
                                    }}
                                  >
                                    {/* Normal içerik */}
                                    <Box className="appt-content">
                                    {/* Satır 1: İkon + Hasta adı */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      {style.icon ? (
                                        <Box
                                          sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 99,
                                            bgcolor: alpha(style.dot, 0.15),
                                            color: style.text,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                          }}
                                        >
                                          {style.icon}
                                        </Box>
                                      ) : null}
                                      <Typography
                                        noWrap
                                        sx={{
                                          fontWeight: 600,
                                          color: style.text,
                                          fontSize: "0.82rem",
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {patientName}
                                      </Typography>
                                      {isInExam ? (
                                        <Box sx={{ ml: "auto", pl: 0.5 }}>
                                          <ExamLiveBadge compact />
                                        </Box>
                                      ) : null}
                                    </Box>

                                    {/* Satır 2: Tip + Saat - ikonlarla tek satır */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.4,
                                        mt: 0.25,
                                      }}
                                    >
                                      <FaStethoscope
                                        style={{
                                          fontSize: 10,
                                          color: style.text,
                                          opacity: 0.8,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        noWrap
                                        variant="caption"
                                        sx={{
                                          fontWeight: 500,
                                          color: style.text,
                                          fontSize: "0.7rem",
                                          opacity: 0.9,
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {typeLabel}
                                      </Typography>
                                      <AccessTimeIcon
                                        sx={{
                                          fontSize: 13,
                                          color: style.text,
                                          opacity: 0.8,
                                          ml: 0.5,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        noWrap
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          color: style.text,
                                          fontSize: "0.7rem",
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {fmtTime(s)} - {fmtTime(e)}
                                      </Typography>
                                    </Box>

                                    {/* Hover'da açılan detaylar */}
                                    <Box className="appt-hover-detail">
                                      {/* Şikayet */}
                                      {a.complaint ? (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: style.text,
                                            opacity: 0.85,
                                            fontSize: "0.72rem",
                                            mt: 0.5,
                                            display: "block",
                                          }}
                                        >
                                          Şikayet: {a.complaint}
                                        </Typography>
                                      ) : null}

                                      {/* İptal nedeni + tarih */}
                                      {isCancelled && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                                          <EventBusyIcon sx={{ fontSize: 12, color: COLORS.error, flexShrink: 0 }} />
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{
                                              color: "#B91C1C",
                                              fontWeight: 700,
                                              fontSize: "0.7rem",
                                            }}
                                          >
                                            {a.cancellation?.reason || "İptal nedeni belirtilmedi"}
                                          </Typography>
                                          {a.cancellation?.cancelledAt && (
                                            <>
                                              <Typography variant="caption" sx={{ color: COLORS.slate400, mx: 0.25 }}>•</Typography>
                                              <Typography
                                                variant="caption"
                                                noWrap
                                                sx={{ color: COLORS.slate400, fontSize: "0.68rem" }}
                                              >
                                                {new Date(a.cancellation.cancelledAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" })}{" "}
                                                {new Date(a.cancellation.cancelledAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                                              </Typography>
                                            </>
                                          )}
                                        </Box>
                                      )}

                                      {/* Notlar */}
                                      {a.notes ? (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: style.text,
                                            opacity: 0.85,
                                            display: "block",
                                            mt: 0.25,
                                            fontSize: "0.72rem",
                                          }}
                                        >
                                          Not: {a.notes}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                    {/* appt-content kapanış */}
                                    </Box>
                                  </Box>
                                  </React.Fragment>
                                );
                              })
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 480, md: 520 },
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
          }}
        >
          <DrawerHeader
            icon={<FaCalendarAlt size={28} color="white" />}
            title="Randevu Oluştur"
            subtitle={`${terms.doctor} ve ${terms.patientLower} seçerek yeni randevu oluşturun.`}
            onClose={() => setCreateOpen(false)}
          />

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormAutocomplete
                options={doctors}
                value={selectedDoctor}
                loading={isDoctorsLoading}
                getOptionLabel={(option) => staffLabel(option)}
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id
                }
                noOptionsText={`${terms.doctor} bulunamadı`}
                loadingText={`${terms.doctor}lar yükleniyor...`}
                onChange={(_, newValue) =>
                  setForm((p) => ({
                    ...p,
                    doctorId: newValue?._id || "",
                  }))
                }
                renderInput={(params) => (
                  <FormTextField
                    {...params}
                    palette={palette}
                    variant="standard"
                    label={terms.doctor}
                    placeholder={`${terms.doctor} ara...`}
                  />
                )}
                sx={{ flex: "1 1 260px" }}
              />
              <FormAutocomplete
                options={patients}
                value={selectedPatient}
                loading={isPatientsLoading}
                getOptionLabel={(option) => {
                  const name = `${option?.name || ""} ${
                    option?.surname || ""
                  }`.trim();
                  const tc = option?.tcNo ? ` (${option.tcNo})` : "";
                  return `${name}${tc}`.trim();
                }}
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id
                }
                noOptionsText={`${terms.patient} bulunamadı`}
                loadingText={`${terms.patients} yükleniyor...`}
                onInputChange={(_, value, reason) => {
                  if (reason === "input") setPatientSearch(value);
                  if (reason === "clear") setPatientSearch("");
                }}
                onChange={(_, newValue) =>
                  setForm((p) => ({
                    ...p,
                    patientId: newValue?._id || "",
                  }))
                }
                renderInput={(params) => (
                  <FormTextField
                    {...params}
                    palette={palette}
                    variant="standard"
                    label={terms.patient}
                    placeholder={`${terms.patient} ara...`}
                  />
                )}
                sx={{ flex: "1 1 260px" }}
              />
            </Box>

            {/* Hasta aynı gün randevu uyarısı */}
            {patientDayConflict && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Typography sx={{ fontSize: TYPO.body, fontWeight: 700 }}>
                  Bu {terms.patientGenLower} seçili tarihte zaten bir randevusu var!
                </Typography>
                <Typography sx={{ fontSize: TYPO.label, mt: 0.5 }}>
                  {patientDayConflict.doctorName} – {patientDayConflict.startTime} / {patientDayConflict.endTime}
                </Typography>
                <Typography sx={{ fontSize: TYPO.caption, mt: 0.3, color: "text.secondary" }}>
                  Bir {terms.patientLower} aynı gün içinde birden fazla randevu alamaz.
                </Typography>
              </Alert>
            )}

            {/* Randevu Tarihi */}
            <TrCalendarPicker
              value={form.date}
              label="Randevu Tarihi"
              onChange={(val) => {
                setForm((p) => ({
                  ...p,
                  date: val,
                  startTime: "",
                  endTime: "",
                }));
              }}
              minDate={toDateStr(new Date())}
              workingSchedule={clinicSchedule}
            />

            {/* Başlangıç & Bitiş Saatleri */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TrTimePicker
                  value={form.startTime}
                  label="Başlangıç Saati"
                  onChange={(val) => {
                    // Otomatik bitiş = başlangıç + 30dk
                    const [h, m] = val.split(":").map(Number);
                    // Hizmet seçildiyse süre hizmetten gelir (billing)
                    const endMin = h * 60 + m + (form.serviceDurationMin || 30);
                    let autoEnd = `${pad2(Math.floor(endMin / 60) % 24)}:${pad2(endMin % 60)}`;

                    // maxTime sınırını aş, mevcut randevuya dikkat
                    const maxAbs = h * 60 + m + Math.max(120, form.serviceDurationMin || 0);
                    let maxM = maxAbs;
                    for (const appt of doctorDayAppointments) {
                      const [ah, am] = appt.start.split(":").map(Number);
                      const apptM = ah * 60 + am;
                      if (apptM > h * 60 + m && apptM < maxM) { maxM = apptM; break; }
                    }
                    const maxEnd = `${pad2(Math.floor(maxM / 60) % 24)}:${pad2(maxM % 60)}`;
                    if (autoEnd > maxEnd) autoEnd = maxEnd;

                    // Çalışma saati sınırı
                    const wEnd = dateSchedule?.endTime || "18:00";
                    if (autoEnd > wEnd) autoEnd = wEnd;

                    setForm((p) => ({
                      ...p,
                      startTime: val,
                      endTime: autoEnd,
                    }));
                  }}
                  startTime={dateSchedule?.isClosed ? null : dateSchedule?.startTime}
                  endTime={dateSchedule?.isClosed ? null : dateSchedule?.endTime}
                  isClosed={dateSchedule?.isClosed || false}
                  blockedSlots={startBlockedSlots}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TrTimePicker
                  value={form.endTime}
                  label="Bitiş Saati"
                  onChange={(val) =>
                    setForm((p) => ({ ...p, endTime: val }))
                  }
                  startTime={dateSchedule?.isClosed ? null : dateSchedule?.startTime}
                  endTime={dateSchedule?.isClosed ? null : dateSchedule?.endTime}
                  isClosed={dateSchedule?.isClosed || false}
                  minTime={endTimeConstraints.minTime}
                  maxTime={endTimeConstraints.maxTime}
                  infoText={endTimeConstraints.infoText}
                  onlyValid
                />
              </Box>
            </Box>

            <FormSelect
              palette={palette}
              variant="standard"
              label="Randevu Tipi"
              value={form.appointmentType}
              onChange={(value) =>
                setForm((p) => ({ ...p, appointmentType: value }))
              }
              options={APPOINTMENT_TYPE_OPTIONS.map((o) =>
                o.value === "ilk_muayene" ? { ...o, label: terms.firstVisitTitle } : o
              )}
              placeholder={null}
            />

            {/* Hizmet + paketten düş (billing modülü; kapalıysa bileşen çizilmez) */}
            <AppointmentServiceFields
              token={token}
              patientId={form.patientId}
              serviceId={form.serviceId}
              patientPackageId={form.patientPackageId}
              onChange={({ serviceId, patientPackageId, service }) =>
                setForm((p) => {
                  const next = { ...p, serviceId, patientPackageId, serviceDurationMin: service?.durationMin || 0 };
                  // Süre hizmetten gelir: bitiş = başlangıç + hizmet süresi
                  if (service?.durationMin && p.startTime) {
                    const [h, m] = p.startTime.split(":").map(Number);
                    const endMin = Math.min(h * 60 + m + service.durationMin, 23 * 60 + 59);
                    next.endTime = `${pad2(Math.floor(endMin / 60))}:${pad2(endMin % 60)}`;
                  }
                  return next;
                })
              }
            />

            {/* Tetkik modülü kapalıysa laboratuvar testi istenmez (spec §7.2) */}
            {investigationModule && (
            <FormAutocomplete
              multiple
              options={labTests}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={form.labTestIds || []}
              onChange={(e, newValue) =>
                setForm((p) => ({
                  ...p,
                  labTestIds: newValue || [],
                  requestLabTest: (newValue || []).length > 0,
                }))
              }
              renderInput={(params) => (
                <FormTextField
                  {...params}
                  palette={palette}
                  variant="standard"
                  label="Laboratuvar Testleri Seç (Opsiyonel)"
                  placeholder="Test seçin..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 800 }}>
                      {option.name}
                    </Typography>
                    {option.unit || option.referenceValue ? (
                      <Typography
                        sx={{ fontSize: TYPO.caption, color: "text.secondary" }}
                      >
                        {[option.unit, option.referenceValue]
                          .filter(Boolean)
                          .join(" • ")}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              )}
              fullWidth
            />
            )}

            <FormTextField
              palette={palette}
              variant="standard"
              label="Şikayet (opsiyonel)"
              value={form.complaint}
              onChange={(e) =>
                setForm((p) => ({ ...p, complaint: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />

            <FormTextField
              palette={palette}
              variant="standard"
              label="Not (opsiyonel)"
              value={form.notes}
              onChange={(e) =>
                setForm((p) => ({ ...p, notes: e.target.value }))
              }
              fullWidth
              multiline
              minRows={3}
            />

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {terms.doctor} için aynı saat aralığında çakışan randevu varsa sistem izin
              vermez.
            </Alert>
          </Box>

          <DrawerFooter
            onCancel={() => setCreateOpen(false)}
            onSubmit={onCreate}
            submitLabel="Oluştur"
            loadingLabel="Kaydediliyor..."
            loading={isCreateLoading}
            disabled={!canCreate}
            startIcon={<AddIcon />}
          />
        </Box>
      </Drawer>

      {/* Randevu Detay Drawer */}
      <AppointmentDetailDrawer
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedAppointment(null);
        }}
        selectedAppointment={selectedAppointment}
        getDoctorName={(id) => {
          const doc = doctors.find((d) => String(d._id) === String(id));
          return doc ? staffLabel(doc) : "-";
        }}
        getRefId={(v) => (v && typeof v === "object" ? v._id : v)}
        getAppointmentTypeStyle={(type) => {
          const t = String(type || "").toLowerCase();
          const map = {
            ilk_muayene: { border: "#2563eb", bg: "#eff6ff", color: "#1d4ed8" },
            kontrol: { border: COLORS.successDark, bg: "#ecfdf5", color: "#15803d" },
          };
          return map[t] || { border: COLORS.slate400, bg: COLORS.slate50, color: COLORS.slate600 };
        }}
        getAppointmentTypeMeta={(type) => {
          const t = String(type || "").toLowerCase();
          const map = {
            ilk_muayene: { label: terms.firstVisitTitle, icon: <FaStethoscope size={14} /> },
            kontrol: { label: "Kontrol", icon: <FaCheckCircle size={14} /> },
          };
          return map[t] || { label: "Randevu", icon: <FaStethoscope size={14} /> };
        }}
        labTests={labTests}
        subtitle={selectedAppointment?.snapshot?.patientName || `${terms.patient} bilgisi`}
        extraContent={
          selectedAppointment?.status === "scheduled" ? (
            <>
              <Box
                sx={{
                  bgcolor: "white",
                  borderRadius: 2.5,
                  border: `1px solid ${COLORS.slate200}`,
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: COLORS.slate400, fontWeight: 700 }}>
                    Şikayet
                  </Typography>
                  <FormTextField
                    palette={palette}
                    variant="standard"
                    value={editComplaint}
                    onChange={(e) => setEditComplaint(e.target.value)}
                    sx={{ mt: 1 }}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Şikayet girin..."
                  />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: COLORS.slate400, fontWeight: 700 }}>
                    Not
                  </Typography>
                  <FormTextField
                    palette={palette}
                    variant="standard"
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    sx={{ mt: 1 }}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Not ekle..."
                  />
                </Box>
              </Box>
              {canEditLabTests && (
                <Box
                  sx={{
                    bgcolor: "white",
                    borderRadius: 2.5,
                    border: `1px solid ${COLORS.slate200}`,
                    px: 2.5,
                    py: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: COLORS.slate400, fontWeight: 700 }}>
                    Laboratuvar Testi Ekle
                  </Typography>
                  <FormAutocomplete
                    multiple
                    options={labTests}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    value={labTestDraft}
                    onChange={(_, newValue) => setLabTestDraft(newValue || [])}
                    renderInput={(params) => (
                      <FormTextField
                        {...params}
                        palette={palette}
                        variant="standard"
                        placeholder="Test ara..."
                        sx={{ mt: 1 }}
                      />
                    )}
                    fullWidth
                  />
                </Box>
              )}
            </>
          ) : undefined
        }
        footer={
          selectedAppointment ? (
            <>
              {selectedAppointment.status === "scheduled" && (
                <Box
                  sx={{
                    p: 3,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    flexShrink: 0,
                  }}
                >
                  <ActionButton
                    variant="save"
                    size="medium"
                    palette={palette}
                    onClick={onUpdateDetails}
                    loading={isUpdateDetailsLoading || isUpdateLabLoading}
                    label={isUpdateDetailsLoading || isUpdateLabLoading ? "Güncelleniyor..." : "Güncelle"}
                  />
                  {canUpdateAppt && (
                    <ActionButton
                      variant="delete"
                      size="medium"
                      onClick={() => openCancelModal(selectedAppointment._id)}
                      loading={isCancelLoading}
                      label={isCancelLoading ? "İptal Ediliyor..." : "İptal Et"}
                    />
                  )}
                </Box>
              )}
              {selectedAppointment.status === "cancelled" && (
                <Box
                  sx={{
                    p: 3,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    flexShrink: 0,
                  }}
                >
                  {canCreateAppt && (
                    <ActionButton
                      variant="add"
                      size="medium"
                      palette={palette}
                      label="Bu Saate Yeni Randevu Oluştur"
                      onClick={() => {
                        const start = new Date(selectedAppointment.startAt);
                        if (isPastDateTime(start)) {
                          setErrorMsg("Geçmiş tarih için randevu oluşturulamaz.");
                          return;
                        }
                        openCreateAt({
                          doctorId: selectedAppointment.doctor,
                          startAt: selectedAppointment.startAt,
                          endAt: selectedAppointment.endAt,
                        });
                        setDetailOpen(false);
                        setSelectedAppointment(null);
                      }}
                    />
                  )}
                  {canDeleteAppt && (
                    <ActionButton
                      variant="delete"
                      size="medium"
                      onClick={() => openDeleteModal(selectedAppointment._id)}
                      loading={isDeleteLoading}
                      label={isDeleteLoading ? "Siliniyor..." : "Randevuyu Kalıcı Sil"}
                    />
                  )}
                </Box>
              )}
            </>
          ) : null
        }
      />

      {/* ───── İptal Onay Modal'ı ───── */}
      <PromptDialog
        open={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setCancelTargetId(null);
        }}
        onConfirm={confirmCancel}
        title="Randevuyu İptal Et"
        message="Bu randevuyu iptal etmek istediğinize emin misiniz? Lütfen iptal nedenini belirtin."
        inputLabel="İptal Nedeni"
        placeholder={`Örn: ${terms.patient} isteği, ${terms.doctorLower} müsait değil...`}
        minRows={2}
        confirmText="İptal Et"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={isCancelLoading}
        loadingText="İptal Ediliyor..."
      />

      {/* ───── Silme Onay Modal'ı ───── */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={confirmDelete}
        title="Randevuyu Kalıcı Olarak Sil"
        message="Bu iptal edilmiş randevuyu kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="Kalıcı Olarak Sil"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={isDeleteLoading}
        loadingText="Siliniyor..."
      />
    </Box>
  );
}
